import React from "react";
import { Button, Input, Label } from "reactstrap";
import RightIcon from "mdi-react/ArrowRightIcon";
import LeftIcon from "mdi-react/ArrowLeftIcon";
import { useTranslation } from "react-i18next";

const IMG_EMOTION_DIR = `${process.env.PUBLIC_URL}/theme/clear/img/feeling/`;
const EMOTIONS = ["happy", "ok", "sad", "angry"];

const FeelingCard = ({ title, description, children, footer }) => (
  <div className="feeling-wizard__panel">
    <div className="feeling-wizard__panel-body">
      <div className="feeling-wizard__panel-header">
        <h2 className="feeling-wizard__title">{title}</h2>
        {description && <p className="feeling-wizard__desc">{description}</p>}
      </div>
      {children}
    </div>
    {footer}
  </div>
);

const FieldError = ({ message }) =>
  message ? <div className="feeling-wizard__error">{message}</div> : null;

const FeelingActions = ({
  showBack = true,
  onBack,
  onNext,
  nextLabel,
  loading = false,
  showNextIcon = true,
}) => {
  const { t } = useTranslation();

  return (
    <div className="feeling-wizard__actions">
      {showBack ? (
        <Button
          type="button"
          className="feeling-wizard__btn feeling-wizard__btn--secondary"
          onClick={onBack}
        >
          <LeftIcon size={16} />
          <span>{t("previous")}</span>
        </Button>
      ) : (
        <span className="feeling-wizard__actions-spacer" />
      )}

      <Button
        type="button"
        className="feeling-wizard__btn feeling-wizard__btn--primary"
        onClick={onNext}
        disabled={loading}
      >
        <span>{loading ? t("please_wait") : nextLabel}</span>
        {!loading && showNextIcon && <RightIcon size={16} />}
      </Button>
    </div>
  );
};

const TextField = ({
  id,
  label,
  value,
  onChange,
  error,
  type = "text",
  placeholder,
  autoComplete,
}) => (
  <div className="feeling-wizard__field">
    <Label className="feeling-wizard__label" for={id}>
      {label}
    </Label>
    <Input
      id={id}
      className={`feeling-wizard__input ${error ? "is-invalid" : ""}`}
      type={type}
      value={value}
      placeholder={placeholder}
      autoComplete={autoComplete}
      onChange={(event) => onChange(id, event.target.value)}
    />
    <FieldError message={error} />
  </div>
);

export const FirstStep = ({ data, errors, onSelectEmotion, onNext }) => {
  const { t } = useTranslation();

  return (
    <FeelingCard
      title={t("how_feeling_today")}
      description={t("feeling_choose_option_desc")}
      footer={<FeelingActions showBack={false} onNext={onNext} nextLabel={t("continue")} />}
    >
      <div className="feeling-wizard__emotion-grid">
        {EMOTIONS.map((emotion) => {
          const selected = data.feeling === emotion;

          return (
            <button
              key={emotion}
              type="button"
              className={`feeling-wizard__emotion ${selected ? "is-selected" : ""}`}
              onClick={() => onSelectEmotion(emotion)}
              aria-pressed={selected}
            >
              <span className="feeling-wizard__emotion-media">
                <img src={`${IMG_EMOTION_DIR}${emotion}.png`} alt={t(emotion)} />
              </span>
              <span className="feeling-wizard__emotion-label">{t(emotion)}</span>
            </button>
          );
        })}
      </div>

      <FieldError message={errors.feeling} />
    </FeelingCard>
  );
};

export const SecondStep = ({ data, errors, onBack, onNext, onFieldChange }) => {
  const { t } = useTranslation();

  return (
    <FeelingCard
      title={t("name")}
      description={t("feeling_name_desc")}
      footer={<FeelingActions onBack={onBack} onNext={onNext} nextLabel={t("continue")} />}
    >
      <div className="feeling-wizard__field-grid">
        <TextField
          id="first_name"
          label={t("first_name")}
          value={data.first_name}
          error={errors.first_name}
          autoComplete="given-name"
          onChange={onFieldChange}
        />
        <TextField
          id="last_name"
          label={t("last_name")}
          value={data.last_name}
          error={errors.last_name}
          autoComplete="family-name"
          onChange={onFieldChange}
        />
      </div>
    </FeelingCard>
  );
};

export const ThirdStep = ({ data, errors, onBack, onNext, onFieldChange }) => {
  const { t } = useTranslation();

  return (
    <FeelingCard
      title={t("email")}
      description={t("feeling_email_desc")}
      footer={<FeelingActions onBack={onBack} onNext={onNext} nextLabel={t("continue")} />}
    >
      <TextField
        id="email"
        label={t("email_address")}
        type="email"
        value={data.email}
        error={errors.email}
        placeholder="example@example.com"
        autoComplete="email"
        onChange={onFieldChange}
      />
    </FeelingCard>
  );
};

export const FourthStep = ({
  data,
  loading,
  onBack,
  onSubmit,
  onFieldChange,
}) => {
  const { t } = useTranslation();

  return (
    <FeelingCard
      title={t("let_us_know")}
      description={t("feeling_comment_desc")}
      footer={
        <FeelingActions
          onBack={onBack}
          onNext={onSubmit}
          nextLabel={t("submit")}
          loading={loading}
          showNextIcon={false}
        />
      }
    >
      <div className="feeling-wizard__field">
        <Label className="feeling-wizard__label" for="comment">
          {t("comment")} <span className="feeling-wizard__optional">({t("optional")})</span>
        </Label>
        <Input
          id="comment"
          className="feeling-wizard__textarea"
          type="textarea"
          rows="6"
          value={data.comment}
          onChange={(event) => onFieldChange("comment", event.target.value)}
          placeholder={t("feeling_comment_desc")}
        />
      </div>
    </FeelingCard>
  );
};
